import { useState } from 'react';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { validationSchemaClientRevoke } from './validationSchemaClientRevoke.ts';
import { useNavigate } from 'react-router';
import { RevokeFormContext } from './RevokeFormClientContext.tsx';
import {
  TRevokeFormValues,
  TFinancialServicesValue,
  TBusinessRelationshipValue,
  TRevokeSubmitData,
} from '../types.ts';
import { initialRevokeFormValues } from '../constants.ts';
import { useAppDispatch } from '~/store/appDispatch.ts';
import { contrAgentType } from '~/constants/directoryList.ts';
import { createApplicationPersonalDataClientRevoke } from '~/store/processing-personal/thunks/personalDataApplicationsThunks.ts';
import { infoMessages } from '~/constants/infoMessages.js';
import { errorMessages } from '~/constants/errorMessages.js';
import SuccessModal from '~/components/modals/SuccessModal/SuccessModal.tsx';
import ModalAlert from '~/components/modals/ModalAlert/ModalAlert';
import { styles } from '~/modules/styles.ts';
import { Box } from '@mui/material';

const PersonalDataRevokeCreateApplicationByClient = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const [isOpenModalAlert, setIsOpenModalAlert] = useState<boolean>(false);
  const [isOpenModalSuccess, setIsOpenModalSuccess] = useState<boolean>(false);
  const [modalAlertText, setModalAlertText] = useState<string>('');
  const [modalSuccessText, setModalSuccessText] = useState<string>('');

  const form = useForm<TRevokeFormValues>({
    defaultValues: initialRevokeFormValues,
    resolver: yupResolver(validationSchemaClientRevoke),
    mode: 'onTouched',
    reValidateMode: 'onChange',
  });

  const handleBack = (): void => {
    navigate('/');
  };
  const closeModalAlert = (): void => setIsOpenModalAlert(false);

  const onSubmit: SubmitHandler<TRevokeFormValues> = async (values) => {
    const financialServicesValue: TFinancialServicesValue = {
      selected: values.financialServices,
      ...(values.financialServices &&
        values.financialServicesClientType && {
          clientType: values.financialServicesClientType.value as string,
        }),
    };

    const businessRelationshipValue: TBusinessRelationshipValue = {
      selected: values.businessRelationship,
      ...(values.businessRelationship && {
        clientType: contrAgentType[1]?.value as string,
      }),
    };

    const data: TRevokeSubmitData = {
      financialServices: financialServicesValue,
      cardAccess: { selected: values.cardAccess },
      businessRelationship: businessRelationshipValue,
      creditBureauData: { selected: values.creditBureauData },
      email: values.email,
      phone: values.phone,
    };

    if (values.otherReasonCheckbox && values.otherReason) {
      data.otherReason = values.otherReason;
    }

    try {
      await dispatch(createApplicationPersonalDataClientRevoke(data)).unwrap();
      setIsOpenModalSuccess(true);
      setModalSuccessText(infoMessages.INSTRUCTIONS_IN_EMAIL);
    } catch {
      setIsOpenModalAlert(true);
      setModalAlertText(errorMessages.SERVICE_UNAVAILABLE);
    }
  };

  return (
    <FormProvider {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <Box sx={styles.pageContainer}>
          <Box sx={styles.formWrapper}>
            <RevokeFormContext />
          </Box>
        </Box>

        {isOpenModalSuccess && (
          <SuccessModal
            isOpen={isOpenModalSuccess}
            text={modalSuccessText}
            onClose={handleBack}
            showButton
            buttonValue={'Готово'}
            dataTestId="modalSuccess"
          />
        )}
        {isOpenModalAlert && (
          <ModalAlert
            isOpen={isOpenModalAlert}
            text={modalAlertText}
            onClose={closeModalAlert}
            dataTestId="modalAlert"
          />
        )}
      </form>
    </FormProvider>
  );
};

export default PersonalDataRevokeCreateApplicationByClient;
